/**
 * Deal Room service - API calls for Phase 6 Shared Deal Rooms
 */
import api from './api';
import { ApiResponse } from '../types';
import {
  DealRoom,
  DealRoomListFilters,
  DealRoomListResponse,
  CreateDealRoomRequest,
  UpdateDealRoomRequest,
  DealRoomDocument,
  DealRoomAccess,
  DealRoomActivity,
  DealRoomMessage,
  GrantAccessRequest,
  UpdateAccessRequest,
  SendMessageRequest,
  UploadDocumentRequest,
} from '../types/dealRoom';

function errMsg(e: any, fallback: string): string {
  return e?.response?.data?.detail || e?.message || fallback;
}

export async function fetchDealRooms(
  filters?: DealRoomListFilters
): Promise<ApiResponse<DealRoomListResponse>> {
  try {
    const response = await api.get('/deal-rooms', { params: filters });
    const payload = response.data;
    if (Array.isArray(payload)) {
      return {
        data: {
          deal_rooms: payload,
          total: payload.length,
          page: 1,
          size: payload.length,
          pages: 1,
        },
      };
    }
    return {
      data: {
        deal_rooms: payload?.deal_rooms || payload?.items || [],
        total: payload?.total ?? 0,
        page: payload?.page ?? 1,
        size: payload?.size ?? 50,
        pages: payload?.pages ?? 1,
      },
    };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to load deal rooms') };
  }
}

export async function fetchDealRoom(id: number): Promise<ApiResponse<DealRoom>> {
  try {
    const response = await api.get(`/deal-rooms/${id}`);
    return { data: response.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to load deal room') };
  }
}

export async function createDealRoom(
  req: CreateDealRoomRequest
): Promise<ApiResponse<DealRoom>> {
  try {
    const response = await api.post('/deal-rooms', req);
    return { data: response.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to create deal room') };
  }
}

export async function updateDealRoom(
  id: number,
  req: UpdateDealRoomRequest
): Promise<ApiResponse<DealRoom>> {
  try {
    const response = await api.patch(`/deal-rooms/${id}`, req);
    return { data: response.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to update deal room') };
  }
}

export async function deleteDealRoom(id: number): Promise<ApiResponse<{ success: true }>> {
  try {
    await api.delete(`/deal-rooms/${id}`);
    return { data: { success: true } };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to delete deal room') };
  }
}

// -------- Documents --------

export async function uploadDocument(
  dealRoomId: number,
  req: UploadDocumentRequest
): Promise<ApiResponse<DealRoomDocument>> {
  try {
    const form = new FormData();
    form.append('file', req.file);
    form.append('access_tier', req.access_tier);
    form.append('file_type', req.file_type);
    if (req.previous_document_id != null) {
      form.append('previous_document_id', String(req.previous_document_id));
    }
    // axios auto-sets multipart/form-data boundary when posting FormData.
    const response = await api.post(`/deal-rooms/${dealRoomId}/documents`, form);
    return { data: response.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to upload document') };
  }
}

export async function fetchDocuments(
  dealRoomId: number,
  asInvestorId?: number
): Promise<ApiResponse<DealRoomDocument[]>> {
  try {
    const response = await api.get(`/deal-rooms/${dealRoomId}/documents`, {
      params: asInvestorId ? { as_investor_id: asInvestorId } : undefined,
    });
    const payload = response.data;
    const docs = Array.isArray(payload)
      ? payload
      : payload?.documents || payload?.items || [];
    return { data: docs };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to load documents') };
  }
}

export async function downloadDocument(
  documentId: number
): Promise<ApiResponse<Blob>> {
  try {
    const response = await api.get(
      `/deal-rooms/documents/${documentId}/download`,
      { responseType: 'blob' }
    );
    return { data: response.data as Blob };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to download document') };
  }
}

// -------- Access --------

export async function grantAccess(
  dealRoomId: number,
  req: GrantAccessRequest
): Promise<ApiResponse<DealRoomAccess>> {
  try {
    const response = await api.post(`/deal-rooms/${dealRoomId}/access`, req);
    return { data: response.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to grant access') };
  }
}

export async function updateAccess(
  accessId: number,
  req: UpdateAccessRequest
): Promise<ApiResponse<DealRoomAccess>> {
  try {
    const response = await api.patch(`/deal-rooms/access/${accessId}`, req);
    return { data: response.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to update access') };
  }
}

export async function revokeAccess(
  accessId: number
): Promise<ApiResponse<{ success: true }>> {
  try {
    await api.delete(`/deal-rooms/access/${accessId}`);
    return { data: { success: true } };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to revoke access') };
  }
}

export async function signNda(
  dealRoomId: number,
  investorId: number
): Promise<ApiResponse<DealRoomAccess>> {
  try {
    const response = await api.post(`/deal-rooms/${dealRoomId}/nda/sign`, {
      investor_id: investorId,
    });
    return { data: response.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to sign NDA') };
  }
}

// -------- Activity --------

export async function fetchActivity(
  dealRoomId: number
): Promise<ApiResponse<DealRoomActivity[]>> {
  try {
    const response = await api.get(`/deal-rooms/${dealRoomId}/activity`);
    const payload = response.data;
    const activities = Array.isArray(payload)
      ? payload
      : payload?.activities || payload?.items || [];
    return { data: activities };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to load activity') };
  }
}

// -------- Messages --------

export async function fetchMessages(
  dealRoomId: number
): Promise<ApiResponse<DealRoomMessage[]>> {
  try {
    const response = await api.get(`/deal-rooms/${dealRoomId}/messages`);
    const payload = response.data;
    const messages = Array.isArray(payload)
      ? payload
      : payload?.messages || payload?.items || [];
    return { data: messages };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to load messages') };
  }
}

export async function sendMessage(
  dealRoomId: number,
  req: SendMessageRequest
): Promise<ApiResponse<DealRoomMessage>> {
  try {
    const response = await api.post(`/deal-rooms/${dealRoomId}/messages`, req);
    return { data: response.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to send message') };
  }
}

// ---------------------------------------------------------------------------
// Read-only view (investor / startup) via signed token (Phase 2)
// ---------------------------------------------------------------------------

const API_BASE =
  process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

export interface ViewLinkResponse {
  token: string;
  url: string;
  party: string;
  expires_in_days: number;
}

/** Admin: mint a shareable read-only link for an investor or the startup. */
export async function createViewLink(
  dealRoomId: number,
  payload: { party: 'investor' | 'founder'; investor_id?: number; ttl_days?: number }
): Promise<ApiResponse<ViewLinkResponse>> {
  try {
    const response = await api.post(
      `/deal-rooms/${dealRoomId}/view-links`,
      payload
    );
    return { data: response.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to create view link') };
  }
}

/** Token-authorized read-only room payload (investor/startup view page). */
export async function fetchView(token: string): Promise<ApiResponse<any>> {
  try {
    const response = await api.get('/deal-room-view', { params: { token } });
    return { data: response.data };
  } catch (e: any) {
    return { error: errMsg(e, 'This deal-room link is invalid or has expired') };
  }
}

/** Direct download URL for a doc via the view token (no app auth). */
export function viewDownloadUrl(docId: number, token: string): string {
  return `${API_BASE}/deal-room-view/documents/${docId}/download?token=${encodeURIComponent(
    token
  )}`;
}

export async function signNdaView(token: string): Promise<ApiResponse<any>> {
  try {
    const response = await api.post(
      '/deal-room-view/nda/sign',
      null,
      { params: { token } }
    );
    return { data: response.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to sign NDA') };
  }
}

export async function postViewMessage(
  token: string,
  body: string
): Promise<ApiResponse<any>> {
  try {
    const response = await api.post('/deal-room-view/messages', { token, body });
    return { data: response.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to send message') };
  }
}

// ---------------------------------------------------------------------------
// Deal strategy (Phase 3)
// ---------------------------------------------------------------------------

export async function fetchStrategy(
  dealRoomId: number
): Promise<ApiResponse<any>> {
  try {
    const response = await api.get(`/deal-rooms/${dealRoomId}/strategy`);
    return { data: response.data };
  } catch (e: any) {
    // 404 = none generated yet; surface as a soft empty, not an error toast.
    if (e?.response?.status === 404) return { data: null };
    return { error: errMsg(e, 'Failed to load strategy') };
  }
}

export async function generateStrategy(
  dealRoomId: number
): Promise<ApiResponse<any>> {
  try {
    const response = await api.post(`/deal-rooms/${dealRoomId}/strategy`);
    return { data: response.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to generate strategy') };
  }
}

const dealRoomService = {
  fetchDealRooms,
  fetchDealRoom,
  createDealRoom,
  updateDealRoom,
  deleteDealRoom,
  uploadDocument,
  fetchDocuments,
  downloadDocument,
  grantAccess,
  updateAccess,
  revokeAccess,
  signNda,
  fetchActivity,
  fetchMessages,
  sendMessage,
  createViewLink,
  fetchView,
  viewDownloadUrl,
  signNdaView,
  postViewMessage,
  fetchStrategy,
  generateStrategy,
};

export default dealRoomService;
